import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { projectService, teamService } from 'api';
import type { TeamMember } from 'api/types/team.types';
import { useSnackbar } from 'notistack';
import React from 'react';
import { MemberPickerDropdown } from 'shared/components/MemberPickerDropdown';
import type { MemberPickerItem } from 'shared/components/MemberPickerDropdown';
import { queryKeys } from 'shared/constants/queryKeys';

interface Props {
  projectId: string;
  /** Already-assigned member user IDs so we can show checked state */
  assignedUserIds: Set<string>;
}

const AddMembersDropdown: React.FC<Props> = ({ projectId, assignedUserIds }) => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const {
    data: workspaceMembers = [],
    isLoading,
    refetch,
  } = useQuery<TeamMember[]>({
    queryKey: queryKeys.team.members(),
    queryFn: () => teamService.getMembers({ status: 'active' }).then((r) => r.data.data),
    enabled: false,
  });

  const addMutation = useMutation({
    mutationFn: (userId: string) => projectService.addProjectMember(projectId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.members(projectId) });
    },
    onError: (e) => enqueueSnackbar(`${(e as Error).message}`, { variant: 'error' }),
  });

  const removeMutation = useMutation({
    mutationFn: (userId: string) => projectService.removeProjectMember(projectId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.members(projectId) });
    },
    onError: () => enqueueSnackbar('Failed to remove member', { variant: 'error' }),
  });

  const handleToggle = (member: MemberPickerItem) => {
    if (assignedUserIds.has(member.userId)) {
      removeMutation.mutate(member.userId);
    } else {
      addMutation.mutate(member.userId);
    }
  };

  const isBusy = (userId: string) =>
    (addMutation.isPending && (addMutation.variables as string) === userId) ||
    (removeMutation.isPending && (removeMutation.variables as string) === userId);

  return (
    <MemberPickerDropdown
      members={workspaceMembers}
      selectedIds={assignedUserIds}
      onToggle={handleToggle}
      isBusy={isBusy}
      isLoading={isLoading}
      onOpen={() => refetch()}
    />
  );
};

export default AddMembersDropdown;
